<template>
  <el-drawer
    :visible.sync="innerVisible"
    direction="rtl"
    size="420px"
    :with-header="false"
    custom-class="device-detail-drawer"
    @open="loadPanel"
  >
    <div v-loading="loading" class="drawer-body">
      <template v-if="panel.device">
        <div class="drawer-head">
          <div>
            <div class="title">{{ panel.device.deviceName }}</div>
            <div class="sub">{{ panel.device.deviceCode }} · {{ panel.device.terminalType || '采集终端' }}</div>
          </div>
          <el-button type="text" icon="el-icon-close" class="close-btn" @click="innerVisible = false" />
        </div>
        <div class="status-row">
          <gas-status-tag kind="online" :value="panel.device.terminalStatus" />
          <span class="time">更新：{{ formatTime(displayLatestTime) }}</span>
        </div>
        <div class="loc">{{ panel.device.siteName || '—' }}</div>

        <div v-if="subDeviceTabs.length > 1" class="sub-device-tabs">
          <button
            v-for="sub in subDeviceTabs"
            :key="sub.code"
            type="button"
            class="sub-tab"
            :class="{ active: selectedSubCode === sub.code }"
            @click="selectedSubCode = sub.code"
          >{{ subDeviceLabel(sub) }}</button>
        </div>

        <div v-if="latestMetrics.length" class="metric-grid">
          <div v-for="m in latestMetrics" :key="m.metricKey" class="metric-card">
            <div class="metric-label">{{ m.label }}</div>
            <div class="metric-value">{{ formatValue(m.value, m.metricKey) }}<small v-if="m.unit">{{ m.unit }}</small></div>
            <div class="metric-time">{{ formatTime(m.sampleTime) }}</div>
          </div>
        </div>
        <div v-else class="empty-data">暂无真实采集数据</div>

        <div v-if="hasPressure" class="gauge-wrap">
          <pressure-dial-gauge
            :value="pressureKpa"
            :trend="dialTrend"
            :min="0"
            :low-limit="panelLowAlarm"
            :high-limit="dialHighLimit"
            :title="dialTitle"
            :subtitle="dialSubtitle"
            :series-label="dialSeriesLabel"
            unit="kPa"
          />
        </div>

        <div class="block">
          <div class="block-title">设备信息</div>
          <div class="kv">项目分类：{{ panel.device.projectCategoryName || '—' }}</div>
          <div class="kv">协议类型：{{ panel.device.protocolType || '—' }}</div>
          <div class="kv">设备地址：{{ panel.device.ip || '—' }}<template v-if="panel.device.port">:{{ panel.device.port }}</template></div>
          <div class="kv">硬件编码：{{ panel.device.hardwareCode || '—' }}</div>
          <div class="kv">超时设定：{{ panel.device.timeoutMinutes != null ? panel.device.timeoutMinutes + '分钟' : '—' }}</div>
        </div>
        <div v-if="hasThresholds" class="block">
          <div class="block-title">报警阈值</div>
          <div v-if="panel.lowAlarm != null" class="kv warn-low">低压力报警 ≤ {{ panel.lowAlarm }} kPa</div>
          <div v-if="panel.highWarning != null" class="kv warn-mid">高压力预警 ≥ {{ panel.highWarning }} kPa</div>
          <div v-if="panel.highAlarm != null" class="kv warn-high">高压力报警 ≥ {{ panel.highAlarm }} kPa</div>
        </div>
        <div class="run-box" :class="{ warn: displayRunStatus !== '运行正常' }">
          <div class="run-title">{{ displayRunStatus }}</div>
          <div class="run-hint">{{ displayRunHint }}</div>
          <div v-if="hasPressure" class="run-percent">{{ pressureMetricLabel }}占比 {{ rangePercent }}%</div>
        </div>

        <div v-if="trendData.length" class="block">
          <div class="block-title row-between">
            <span>{{ pressureMetricLabel }}趋势</span>
            <el-radio-group v-model="trendRange" size="mini">
              <el-radio-button label="6">6小时</el-radio-button>
              <el-radio-button label="24">24小时</el-radio-button>
              <el-radio-button label="168">7天</el-radio-button>
            </el-radio-group>
          </div>
          <div class="trend-chart">
            <svg viewBox="0 0 360 120" class="trend-svg">
              <polyline :points="trendPoints" fill="none" stroke="#1677ff" stroke-width="2" />
              <polygon :points="trendArea" fill="rgba(22,119,255,0.12)" stroke="none" />
            </svg>
          </div>
        </div>
      </template>
    </div>
  </el-drawer>
</template>

<script>
import { gasApi } from '@/api/gas'
import PressureDialGauge from '@/components/PressureDialGauge.vue'
import { formatMonitorMetricValue } from '@/config/deviceTypeMetrics'

export default {
  name: 'DeviceDetailDrawer',
  components: { PressureDialGauge },
  props: {
    visible: { type: Boolean, default: false },
    deviceId: { type: [Number, String], default: null }
  },
  data() {
    return {
      loading: false,
      panel: {},
      trendRange: '24',
      selectedSubCode: ''
    }
  },
  computed: {
    innerVisible: {
      get() { return this.visible },
      set(v) { this.$emit('update:visible', v) }
    },
    dialTrend() {
      const source = this.activeSub ? this.activeSub.trend24h : this.panel.trend24h
      return (source || []).filter(p => Number.isFinite(Number(p && p.value)))
    },
    dialHighLimit() {
      if (this.panelHighAlarm != null) return this.panelHighAlarm
      return this.panelHighWarning
    },
    dialTitle() {
      const d = this.panel.device || {}
      return d.siteName || d.deviceName || ''
    },
    dialSubtitle() {
      const d = this.panel.device || {}
      if (!d.deviceCode) return ''
      return `设备号：${d.deviceCode}`
    },
    dialSeriesLabel() {
      const name = this.activeSub ? this.subDeviceLabel(this.activeSub) : '设备1'
      return `${name}：${this.pressureMetricLabel}`
    },
    panelLowAlarm() {
      const n = Number(this.panel.lowAlarm)
      return Number.isFinite(n) ? n : null
    },
    panelHighWarning() {
      const n = Number(this.panel.highWarning)
      return Number.isFinite(n) ? n : null
    },
    panelHighAlarm() {
      const n = Number(this.panel.highAlarm)
      return Number.isFinite(n) ? n : null
    },
    subDeviceTabs() {
      const list = this.panel.subDevices || []
      return list.length > 1 ? list : []
    },
    subDeviceNameMap() {
      const d = this.panel.device || {}
      const raw = this.panel.subDeviceNames || d.subDeviceNames || {}
      if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw
      if (typeof raw === 'string' && raw.trim()) {
        try {
          const parsed = JSON.parse(raw)
          return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
        } catch (e) {
          return {}
        }
      }
      return {}
    },
    displayRunStatus() {
      if (this.activeSub && this.activeSub.runStatus) return this.activeSub.runStatus
      return this.panel.runStatus
    },
    displayRunHint() {
      if (this.activeSub && this.activeSub.runHint) return this.activeSub.runHint
      return this.panel.runHint
    },
    activeSub() {
      const tabs = this.subDeviceTabs
      if (!tabs.length) return null
      return tabs.find(s => s.code === this.selectedSubCode) || tabs[0]
    },
    displayLatestTime() {
      if (this.activeSub && this.activeSub.latestTime) return this.activeSub.latestTime
      return this.panel.latestTime || (this.panel.device && this.panel.device.updateTime)
    },
    latestMetrics() {
      if (this.activeSub) return this.activeSub.latestMetrics || []
      return this.panel.latestMetrics || []
    },
    pressureKpa() {
      return this.activeSub ? this.activeSub.pressureKpa : this.panel.pressureKpa
    },
    hasPressure() {
      const n = Number(this.pressureKpa)
      return Number.isFinite(n)
    },
    rangePercent() {
      if (this.activeSub && this.activeSub.rangePercent != null) return this.activeSub.rangePercent
      return this.panel.rangePercent
    },
    hasThresholds() {
      return this.panel.lowAlarm != null || this.panel.highWarning != null || this.panel.highAlarm != null
    },
    pressureMetricLabel() {
      if (this.activeSub && this.activeSub.pressureMetricLabel) return this.activeSub.pressureMetricLabel
      return this.panel.pressureMetricLabel || '压力'
    },
    trendData() {
      const source = this.activeSub ? this.activeSub.trend24h : this.panel.trend24h
      const arr = (source || []).filter(p => Number.isFinite(Number(p.value)))
      const hours = Number(this.trendRange) || 24
      const cutoff = Date.now() - hours * 3600 * 1000
      const filtered = arr.filter(p => {
        const t = Date.parse(p.time || p.sampleTime || '')
        return Number.isNaN(t) ? true : t >= cutoff
      })
      return filtered.length ? filtered : arr
    },
    trendPoints() {
      return this.buildSvgPoints(false)
    },
    trendArea() {
      const line = this.buildSvgPoints(false)
      if (!line) return ''
      return `0,120 ${line} 360,120`
    }
  },
  watch: {
    deviceId() {
      if (this.innerVisible) this.loadPanel()
    }
  },
  methods: {
    async loadPanel() {
      if (!this.deviceId) return
      this.loading = true
      try {
        const res = await gasApi.device.detailPanel(this.deviceId)
        this.panel = res.data || {}
        const tabs = this.subDeviceTabs
        const first = tabs.find(s => s.code === 'm1') || tabs[0]
        const stillValid = tabs.some(s => s.code === this.selectedSubCode)
        this.selectedSubCode = stillValid ? this.selectedSubCode : ((first && first.code) || '')
      } finally {
        this.loading = false
      }
    },
    subDeviceLabel(sub) {
      const code = sub && sub.code ? String(sub.code) : ''
      const names = this.subDeviceNameMap || {}
      const custom = String(names[code] || names[code.toLowerCase()] || names[code.toUpperCase()] || '').trim()
      if (custom) return custom
      if (sub && sub.label) return sub.label
      const n = code.replace(/^m/i, '')
      return n ? ('设备' + n) : '设备1'
    },
    formatValue(v, metricKey) {
      return formatMonitorMetricValue(v, metricKey)
    },
    formatTime(v) {
      if (!v) return '—'
      return String(v).replace('T', ' ').slice(0, 19)
    },
    buildSvgPoints() {
      const data = this.trendData
      if (!data.length) return ''
      const values = data.map(d => Number(d.value)).filter(Number.isFinite)
      if (!values.length) return ''
      let max = Math.max(...values)
      let min = Math.min(...values)
      if (max === min) {
        max += 1
        min -= 1
      }
      const pad = (max - min) * 0.15
      max += pad
      min -= pad
      return data.map((d, i) => {
        const x = (i / Math.max(1, data.length - 1)) * 360
        const v = Number(d.value)
        const y = 120 - ((v - min) / (max - min)) * 100
        return `${x},${y}`
      }).join(' ')
    }
  }
}
</script>

<style scoped>
.drawer-body {
  padding: 20px 20px 32px;
  min-height: 100%;
}
.drawer-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}
.title {
  font-size: 18px;
  font-weight: 600;
  color: #1d2129;
}
.sub {
  font-size: 13px;
  color: #86909c;
  margin-top: 4px;
}
.close-btn {
  font-size: 18px;
  padding: 0;
}
.status-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 12px 0 4px;
}
.time {
  font-size: 12px;
  color: #86909c;
}
.loc {
  font-size: 13px;
  color: #4e5969;
  margin-bottom: 16px;
}
.sub-device-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: -4px 0 14px;
}
.sub-tab {
  border: 1px solid #d9d9d9;
  background: #fff;
  color: #4e5969;
  border-radius: 12px;
  padding: 2px 10px;
  font-size: 12px;
  line-height: 20px;
  cursor: pointer;
}
.sub-tab.active {
  border-color: #1677ff;
  background: #e6f4ff;
  color: #1677ff;
}
.metric-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 16px;
}
.metric-card {
  border: 1px solid #e5e6eb;
  border-radius: 6px;
  padding: 10px;
  background: #fff;
}
.metric-label {
  font-size: 12px;
  color: #86909c;
}
.metric-value {
  margin-top: 4px;
  font-size: 18px;
  font-weight: 700;
  color: #1d2129;
}
.metric-value small {
  margin-left: 4px;
  font-size: 11px;
  font-weight: 400;
  color: #86909c;
}
.metric-time {
  margin-top: 4px;
  font-size: 11px;
  color: #86909c;
}
.empty-data {
  border: 1px dashed #c9cdd4;
  border-radius: 6px;
  color: #86909c;
  text-align: center;
  padding: 18px 8px;
  margin-bottom: 16px;
}
.gauge-wrap {
  display: flex;
  justify-content: center;
  margin: 4px 0 16px;
}
.block {
  margin-bottom: 16px;
}
.block-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #1d2129;
}
.row-between {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.kv {
  font-size: 13px;
  color: #4e5969;
  line-height: 1.8;
}
.warn-low { color: #ff7d00; }
.warn-mid { color: #ff7d00; }
.warn-high { color: #f53f3f; }
.run-box {
  background: #e8f3ff;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 16px;
}
.run-box.warn {
  background: #fff7e8;
}
.run-title {
  font-weight: 600;
  color: #1677ff;
}
.run-box.warn .run-title {
  color: #ff7d00;
}
.run-hint {
  font-size: 13px;
  color: #4e5969;
  margin-top: 4px;
}
.run-percent {
  font-size: 12px;
  color: #86909c;
  margin-top: 6px;
}
.trend-chart {
  background: #f7f8fa;
  border-radius: 8px;
  padding: 8px;
}
.trend-svg {
  width: 100%;
  height: 120px;
}
</style>
